import React, { useEffect, useState, useRef } from "react";

//routing
import { useParams, useNavigate } from "react-router-dom";

//schemas
import form from "./schema/form";
import validation from "./schema/validation";
import initialValues from "./schema/initialValues";

//external components
import { Box, Card, Grid, CircularProgress } from "@mui/material";

//components
import IALoadingButton from "../../../../components/IAButtons/IALoadingButton";

//pages
import Main from "./components/main";

// formik components
import { Formik, Form } from "formik";

//translation
import { useTranslation } from "react-i18next";

//hooks
import useQueryAuth from "../../../../hooks/useQueryAuth";
import useMutationAuth from "../../../../hooks/useMutationAuth";
import { useSnackbar } from "notistack";
import useAuth from "../../../../hooks/useAuth";

import ContainerCard from "../../../../components/Cards/ContainerCard";

import {
  CREATE_SUGGESTION,
  GET_SUGGESTION_BY_ID,
  UPDATE_SUGGESTION,
} from "../../../../graphql/suggestions";
import urls from "../../../../urls";

const SuggestionForm = ({
  handleCloseModal,
  refetchData,
  fromDetails,
  fromNav,
  suggGuid,
}) => {
  const { sName, sID } = useParams();

  const createMode = (!sName && !sID && !suggGuid) || fromNav;

  const { storeUserGuid, userRoles } = useAuth();

  const { manageParameters: manageParametersRole, isEntelligence } = userRoles;

  const { suggestions_list } = urls;

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { formField } = form;

  const { description, suggestionStatus } = formField;

  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState(initialValues);

  const [whatField, setWhatField] = useState(null);
  const [getByID, setGetById] = useState(null);

  const formikValuesRef = useRef(null);
  const formikRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl + S
      if (event.key === 's' && event.ctrlKey) {
        event.preventDefault(); // Prevent the default behavior
        formikRef.current?.handleSubmit(formikValuesRef.current); // Submit the form
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
  
    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const { loading: getDataByIDLoading } = useQueryAuth(
    GET_SUGGESTION_BY_ID,
    "GetSuggestionByID",
    {
      variables: {
        model: { suggestionGuid: sID || suggGuid, employeeGuid: storeUserGuid },
      },
      skip: createMode,
      isWait: !createMode,
    },
    {
      onCompleted: (response) => {
        const data =
          response?.GetSuggestionByID?.suggestionView?.suggestion || {};

        setFormData({
          [description.name]: data?.description || "",
          [suggestionStatus.name]: data?.suggestionStatus || "",
        });
        setGetById(response?.GetSuggestionByID?.suggestionView);
      },
    }
  );
  const { cfunction: editSuggestionData } = useMutationAuth(
    UPDATE_SUGGESTION,
    "UpdateSuggestion"
  );

  const { cfunction: createSuggestionData } = useMutationAuth(
    CREATE_SUGGESTION,
    "CreateSuggestion"
  );

  function createSuggestion(values, actions) {
    createSuggestionData(
      {
        variables: {
          model: {
            description: values[description.name],
            createdByGuid: storeUserGuid,
          },
        },
      },
      (response) => {
        if (
          response["CreateSuggestion"].errorCode !== null &&
          response["CreateSuggestion"].errorCode !== ""
        ) {
          enqueueSnackbar(t(response["CreateSuggestion"].errorMessage), {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(t("Suggestion.createdSuccessfully"), {
            variant: "success",
          });
          sessionStorage.removeItem("unsavedChanges");

          setTimeout(() => {
            refetchData();
            handleCloseModal(false);
          }, 200);
        }
        actions.setSubmitting(false);
      },
      (error) => {
        actions.setSubmitting(false);
      }
    );
  }

  function editSuggestion(values, actions) {
    editSuggestionData(
      {
        variables: {
          model: {
            suggestionGuid: sID || suggGuid,
            suggestionStatusGuid:
              values[suggestionStatus.name]?.suggestionStatusGuid,
            description: values[description.name],
          },
        },
      },
      (response) => {
        if (
          response["UpdateSuggestion"].errorCode !== null &&
          response["UpdateSuggestion"].errorCode !== ""
        ) {
          enqueueSnackbar(t(response["UpdateSuggestion"].errorMessage), {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(t("roleForm.updatedSuccessfully"), {
            variant: "success",
          });
          sessionStorage.removeItem("unsavedChanges");
          handleCloseModal(false);
        }
        actions.setSubmitting(false);
      },
      (error) => {
        actions.setSubmitting(false);
      }
    );
  }

  const handleSubmit = (values, actions) => {
    if (createMode) {
      createSuggestion(values, actions);
    } else {
      editSuggestion(values, actions);
    }
  };

  return (
    <>
      {getDataByIDLoading ? (
        <ContainerCard sx={{ height: "50vh" }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
          >
            <CircularProgress />
          </Box>
        </ContainerCard>
      ) : (
        <Box mb={5}>
          <Grid
            container
            justifyContent="left"
            alignItems="stretch"
            sx={{ height: "100%" }}
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={fromDetails ? 7 : 12}
              lg={fromDetails ? 7 : 12}
            >
              <ContainerCard>
                <Formik
                  innerRef={formikRef}
                  key={`${form.formID}-form-${
                    createMode ? "creation" : "edit"
                  }`}
                  initialValues={formData}
                  validationSchema={validation[0]}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    values,
                    errors,
                    touched,
                    isSubmitting,
                    setFieldValue,
                    setTouched,
                  }) =>  {
                    formikValuesRef.current = values;
                    return (
                    <Form
                      id={form.formID}
                      autoComplete="off"
                      onChange={() => {
                        if (touched)
                          sessionStorage.setItem(
                            "unsavedChanges",
                            JSON.stringify(true)
                          );
                      }}
                    >
                      <Box p={fromDetails ? 3 : 0}>
                        <Box>
                          <Main
                            suggGuid={suggGuid}
                            getByID={getByID}
                            getDataByIDLoading={getDataByIDLoading}
                            formData={{
                              values,
                              fromDetails,
                              touched,
                              formField: form.formField,
                              errors,
                              setFieldValue,
                              setTouched,
                              createMode,
                              handleCloseModal,
                              whatField,
                              setWhatField,
                              readOnly: manageParametersRole === "R",
                            }}
                          />
                        </Box>
                        {(isEntelligence || createMode) && (
                          <Box
                            mt={2}
                            width="100%"
                            display="flex"
                            justifyContent="flex-end"
                          >
                            <IALoadingButton
                              loading={isSubmitting}
                              type="submit"
                              label={t("sugg.submit")}
                            />
                          </Box>
                        )}
                      </Box>
                    </Form>
                  )}}
                </Formik>
              </ContainerCard>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default SuggestionForm;
